import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Table from '../common/Table';
import DateRangePicker from '../common/DateRangePicker';
import Input from '../common/Input';
import Button from '../common/Button';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatters';

const DEFAULT_PAGINATION = {
  page:  1,
  limit: 50,
  total: 0,
  pages: 0
};

const DEFAULT_FILTERS = {
  dateRange: {
    startDate: new Date(new Date().setDate(1)),
    endDate:   new Date()
  },
  action: ''
};

const BusinessAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          page:  pagination.page,
          limit: pagination.limit,
          from_date: filters.dateRange.startDate instanceof Date
            ? filters.dateRange.startDate.toISOString().split('T')[0]
            : filters.dateRange.startDate,
          to_date: filters.dateRange.endDate instanceof Date
            ? filters.dateRange.endDate.toISOString().split('T')[0]
            : filters.dateRange.endDate,
        };

        if (filters.action) params.action = filters.action;

        const response = await api.get('/audit.php', { params });

        setLogs(response.data.logs || []);
        setPagination(prev => ({
          ...DEFAULT_PAGINATION,
          ...prev,
          ...(response.data.pagination || {}),
          page: pagination.page
        }));
      } catch (err) {
        console.error('Failed to load audit logs:', err);
        setError('Failed to load audit logs. Please try again.');
        setPagination(prev => ({ ...DEFAULT_PAGINATION, ...prev }));
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [pagination.page]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPagination(DEFAULT_PAGINATION);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const columns = [
    {
      key: 'created_at',
      label: 'Timestamp',
      render: (value) => formatDateTime(value),
      width: '180px'
    },
    {
      key: 'user',
      label: 'User',
      render: (_, row) => (
        <div>
          <div>{row.first_name} {row.last_name}</div>
          <div className="text-small" style={{ fontSize: '0.8rem', color: 'gray' }}>{row.username}</div>
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => (
        <span className={`action-badge ${value}`} style={{ 
          background: 'var(--primary-light)', 
          color: 'var(--primary-color)',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'entity_type',
      label: 'Entity',
      render: (value, row) => (
        <div>
          <div>{value || '-'}</div>
          <div className="text-small" style={{ fontSize: '0.8rem', color: 'gray' }}>ID: {row.entity_id || '-'}</div>
        </div>
      )
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      render: (value) => value || '-'
    }
  ];

  const currentPage  = pagination?.page  ?? 1;
  const totalPages   = pagination?.pages ?? 0;
  const totalEntries = pagination?.total ?? 0;
  const limit        = pagination?.limit ?? 50;
  const showingFrom  = totalEntries === 0 ? 0 : ((currentPage - 1) * limit) + 1;
  const showingTo    = Math.min(currentPage * limit, totalEntries);

  return (
    <div className="audit-logs">
      <div className="filters-grid" style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '250px' }}>
          <label>Date Range</label>
          <DateRangePicker
            startDate={filters.dateRange.startDate}
            endDate={filters.dateRange.endDate}
            onChange={(range) => setFilters({ ...filters, dateRange: range })}
          />
        </div>

        <div style={{ flex: 1, minWidth: '200px' }}>
          <Input
            label="Action Type"
            type="text"
            placeholder="e.g., login, create_sale"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            style={{ marginBottom: 0 }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant="primary" onClick={handleSearch}>
            Filter
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {error && (
        <div className="alert error" style={{ marginBottom: '15px' }}>
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={logs}
        loading={loading}
        emptyMessage="No audit logs found for the selected filters."
      />

      {totalEntries > 0 && (
        <div className="table-footer" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', alignItems: 'center' }}>
          <div className="pagination-info" style={{ color: 'gray', fontSize: '0.9rem' }}>
            Showing {showingFrom} to {showingTo} of {totalEntries} entries
          </div>
          <div className="pagination-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <span className="page-indicator" style={{ fontSize: '0.9rem' }}>
              Page {currentPage} of {totalPages || 1}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessAuditLogs;
